import React, { useEffect, useState } from "react";
import { Checkbox, Input } from "antd";
import { InfoCircleOutlined, MailOutlined } from "@ant-design/icons";
import styled from "styled-components";
import dayjs from "dayjs";
import type { CampaignEngagementTerms, EngagementCreator } from "@/api/engagementTerms";
import { DynamicModal } from "@/shared/components/Modal/DynamicModal";
import { CharacterCountTextArea } from "@/shared/components/Textarea/CharacterCountTextArea";
import { snapshotFromResponse, type CetSnapshot } from "../../hooks/engagement-terms-tab/engagementTermsState";
import { normalizeEmail } from "@/shared/utils/email";

const ModalBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

const Warning = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 12px;
	padding: 16px;
	border: 1px solid #f0a20b;
	border-radius: 16px;
	background: #fff9ed;
	color: #303030;
	font-size: 16px;
	font-weight: 600;
	line-height: 1.5;

	.anticon {
		flex: 0 0 auto;
		margin-top: 2px;
		color: #f0a20b;
		font-size: 22px;
	}
`;

const ChangesHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	color: #222;
	font-size: 18px;
	font-weight: 700;
`;
const ChangesCount = styled.span`
	padding: 1px 5px;
	border-radius: 24px;
	background: #e9e2fd;
	color: #3a0ca3;
	font-size: 12px;
	font-weight: 500;
`;
const DiffTable = styled.div`
	overflow: hidden;
	border: 1px solid #cdd4f6;
	border-radius: 12px;
	background: #fff;
`;
const DiffRow = styled.div<{ $header?: boolean }>`
	display: grid;
	grid-template-columns: 170px 150px 24px minmax(0, 1fr);
	align-items: center;
	gap: 16px;
	min-height: ${(props) => (props.$header ? "54px" : "45px")};
	padding: 8px 16px;
	border-top: ${(props) => (props.$header ? "0" : "1px solid #e6e6e6")};
	background: ${(props) => (props.$header ? "#f3f5ff" : "#fff")};
	color: ${(props) => (props.$header ? "#707070" : "#454545")};
	font-size: ${(props) => (props.$header ? "12px" : "14px")};
`;
const VersionHeading = styled.div<{ $current?: boolean }>`
	display: grid;
	color: ${(props) => (props.$current ? "#3a0ca3" : "#707070")};
	font-weight: 500;

	small {
		font-size: 11px;
		font-weight: 400;
	}
`;
const OldValue = styled.span`
	color: #707070;
	text-decoration: line-through;
`;
const NewValue = styled.span`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	color: #3a0ca3;
	font-weight: 600;
`;
const ChangePill = styled.span`
	padding: 2px 8px;
	border-radius: 24px;
	background: #e9e2fd;
	font-size: 12px;
	font-weight: 500;
`;

const Fields = styled.div`
	display: grid;
	gap: 16px;
`;

const Field = styled.label<{ $invalid?: boolean }>`
	display: grid;
	gap: 6px;
	color: #707070;
	font-size: 14px;
	font-weight: 400;

	.ant-input {
		border-color: ${(props) => (props.$invalid ? "#f72586" : "#cdd4f6")} !important;
		border-radius: 8px;
		background: #fff;
		font-size: 15px;

		&:hover,
		&:focus {
			border-color: #3a0ca3 !important;
			box-shadow: 0 0 0 2px rgba(58, 12, 163, 0.1) !important;
		}
	}

	input.ant-input {
		height: 44px;
		padding: 0 12px;
	}
`;

const InlineError = styled.span`
	color: #d80027;
	font-size: 11px;
	line-height: 14px;
`;

interface Props {
	open: boolean;
	creator: EngagementCreator | null;
	email: string;
	message: string;
	sendReminder: boolean;
	sending: boolean;
	onEmailChange: (email: string) => void;
	onMessageChange: (message: string) => void;
	onSendReminderChange: (checked: boolean) => void;
	onCancel: () => void;
	onConfirm: () => void;
	version: CampaignEngagementTerms | null;
	previousVersion: CampaignEngagementTerms | null;
	draftSnapshot: CetSnapshot;
}

export const SendEngagementTermsModal: React.FC<Props> = ({
	open,
	creator,
	email,
	message,
	sendReminder,
	sending,
	onEmailChange,
	onMessageChange,
	onSendReminderChange,
	onCancel,
	onConfirm,
	version,
	previousVersion,
	draftSnapshot,
}) => {
	const [emailTouched, setEmailTouched] = useState(false);
	const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(email));
	const changes = React.useMemo(() => {
		if (!previousVersion) return [];
		const previous = snapshotFromResponse(previousVersion);
		const rows: Array<{ field: string; oldValue: string; newValue: string; tag: string }> = [];
		const date = (value: string) => (value ? dayjs(value).format("MMMM D, YYYY") : "—");
		if (previous.startDate !== draftSnapshot.startDate) {
			rows.push({
				field: "Effective Start Date",
				oldValue: date(previous.startDate),
				newValue: date(draftSnapshot.startDate),
				tag: "Changed",
			});
		}
		if (previous.endDate !== draftSnapshot.endDate) {
			rows.push({
				field: "Effective End Date",
				oldValue: date(previous.endDate),
				newValue: date(draftSnapshot.endDate),
				tag: "Extended",
			});
		}
		if (previous.amount !== draftSnapshot.amount || previous.currency !== draftSnapshot.currency) {
			rows.push({
				field: "Creator Fee",
				oldValue: `${previous.currency} ${previous.amount || "0"}`,
				newValue: `${draftSnapshot.currency} ${draftSnapshot.amount || "0"}`,
				tag:
					Number(draftSnapshot.amount.replace(/,/g, "")) > Number(previous.amount.replace(/,/g, ""))
						? "Increased"
						: "Changed",
			});
		}
		if (previous.paymentTerms !== draftSnapshot.paymentTerms) {
			rows.push({
				field: "Payment Terms",
				oldValue: previous.paymentTerms || "—",
				newValue: draftSnapshot.paymentTerms || "—",
				tag: "Changed",
			});
		}
		if (JSON.stringify(previous.deliverables) !== JSON.stringify(draftSnapshot.deliverables)) {
			rows.push({
				field: "Deliverables",
				oldValue: `${previous.deliverables.length} ${previous.deliverables.length === 1 ? "item" : "items"}`,
				newValue: `${draftSnapshot.deliverables.length} ${draftSnapshot.deliverables.length === 1 ? "item" : "items"}`,
				tag: draftSnapshot.deliverables.length > previous.deliverables.length ? "Added" : "Changed",
			});
		}
		if (
			previous.exclusivityEnabled !== draftSnapshot.exclusivityEnabled ||
			previous.exclusivityPeriod !== draftSnapshot.exclusivityPeriod ||
			previous.exclusivityCategory !== draftSnapshot.exclusivityCategory ||
			JSON.stringify(previous.brands) !== JSON.stringify(draftSnapshot.brands)
		) {
			rows.push({
				field: "Exclusivity",
				oldValue: previous.exclusivityPeriod || "None",
				newValue: draftSnapshot.exclusivityPeriod || "None",
				tag: "Changed",
			});
		}
		return rows;
	}, [draftSnapshot, previousVersion]);

	useEffect(() => {
		if (open) setEmailTouched(false);
	}, [open]);

	const confirm = () => {
		setEmailTouched(true);
		if (!emailValid) return;
		onConfirm();
	};

	return (
		<DynamicModal
			open={open}
			width={761}
			onCancel={onCancel}
			onConfirm={confirm}
			title={`Send “Version ${version?.versionNumber || 1}” to Creator`}
			icon={<MailOutlined style={{ fontSize: 36 }} />}
			confirmText="Send to Creator"
			cancelText="Cancel"
			showCloseButton={false}
			actionHeight={48}
			fullWidthActions
			scrollable
			maxHeight="calc(100vh - 96px)"
		>
			<ModalBody>
				<Warning>
					<InfoCircleOutlined />
					<span>
						{previousVersion
							? `Version ${previousVersion.versionNumber} stays in force until Creator accepts Version ${version?.versionNumber || previousVersion.versionNumber + 1}.`
							: "This Creator’s Engagement Terms will be locked after this point."}
					</span>
				</Warning>

				{previousVersion && (
					<div>
						<ChangesHeader>
							<span>What changed</span>
							<ChangesCount>
								{changes.length} {changes.length === 1 ? "field" : "fields"} updated
							</ChangesCount>
						</ChangesHeader>
						<DiffTable>
							<DiffRow $header>
								<span>Field</span>
								<VersionHeading>
									Version {previousVersion.versionNumber}
									<small>
										{previousVersion.respondedAt
											? `accepted ${dayjs(previousVersion.respondedAt).format("MMM D, YYYY")}`
											: "previous"}
									</small>
								</VersionHeading>
								<span />
								<VersionHeading $current>
									Version {version?.versionNumber || previousVersion.versionNumber + 1}
									<small>new — awaiting acceptance</small>
								</VersionHeading>
							</DiffRow>
							{changes.map((change) => (
								<DiffRow key={change.field}>
									<span>{change.field}</span>
									<OldValue>{change.oldValue}</OldValue>
									<span>→</span>
									<NewValue>
										{change.newValue}
										<ChangePill>{change.tag}</ChangePill>
									</NewValue>
								</DiffRow>
							))}
						</DiffTable>
					</div>
				)}

				<Fields>
					<Field $invalid={emailTouched && !emailValid}>
						<span>Email Address</span>
						<Input
							type="email"
							value={email}
							placeholder="creator@email.com"
							disabled={creator?.isReturningCreator}
							onChange={(event) => {
								onEmailChange(event.target.value);
								if (emailTouched) setEmailTouched(false);
							}}
						/>
						{emailTouched && !emailValid && <InlineError>Enter a valid email address</InlineError>}
					</Field>
					<Field>
						<span>Message (Optional)</span>
						<CharacterCountTextArea
							value={message}
							maxLength={255}
							autoSize={{ minRows: 4, maxRows: 4 }}
							onChange={(event) => onMessageChange(event.target.value)}
						/>
					</Field>
					<Checkbox
						checked={sendReminder}
						disabled={sending}
						onChange={(event) => onSendReminderChange(event.target.checked)}
					>
						Limit reminders to one email after 7 days.
					</Checkbox>
				</Fields>
			</ModalBody>
		</DynamicModal>
	);
};
